import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import {
  getBusinessNotifications,
  getBusinessNotificationsUnreadCount,
  markAllBusinessNotificationsRead,
  markBusinessNotificationRead
} from './api';
import { NotificationsListResponse, UnreadCountResponse } from './types';

const qk = {
  list: (businessId: number | null) => ['business_notifications', businessId] as const,
  unread: (businessId: number | null) => ['business_notifications_unread', businessId] as const
};

export function useBusinessNotifications(
  businessId: number | null,
  params: { page?: number; per_page?: number; unread_only?: boolean } = {},
  options?: Omit<UseQueryOptions<NotificationsListResponse, Error>, 'queryKey' | 'queryFn'>
) {
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery<NotificationsListResponse, Error>({
    queryKey: [...qk.list(businessId), params],
    queryFn: () => getBusinessNotifications(params),
    enabled: Boolean(businessId) && (enabledOption ?? true),
    ...rest
  });
}

export function useBusinessNotificationsUnreadCount(
  businessId: number | null,
  options?: Omit<UseQueryOptions<UnreadCountResponse, Error>, 'queryKey' | 'queryFn'>
) {
  const { enabled: enabledOption, ...rest } = options ?? {};
  return useQuery<UnreadCountResponse, Error>({
    queryKey: qk.unread(businessId),
    queryFn: () => getBusinessNotificationsUnreadCount(),
    enabled: Boolean(businessId) && (enabledOption ?? true),
    // refetchInterval: 10_000,
    ...rest
  });
}

export function useBusinessNotificationMutations(businessId: number | null) {
  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: qk.list(businessId) });
    queryClient.invalidateQueries({ queryKey: qk.unread(businessId) });
  };

  const markRead = useMutation({
    mutationFn: (id: number) => markBusinessNotificationRead(id),
    onSuccess: invalidate
  });

  const markAllRead = useMutation({
    mutationFn: () => markAllBusinessNotificationsRead(),
    onSuccess: invalidate
  });

  return { markRead, markAllRead };
}

export { qk as businessNotificationQueryKeys };
